import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { 
  HiOutlinePlus, HiOutlineSearch, HiOutlinePencil, HiOutlineTrash, 
  HiOutlineMail, HiOutlinePhone, HiOutlineUser, HiOutlineCalendar,
  HiOutlineExternalLink,
  HiOutlineEye
} from 'react-icons/hi';
import LoadingSpinner from '../components/LoadingSpinner';
import { Contact, Company } from '../types';
import { contactsService } from '../services/contactsService';
import { companiesService } from '../services/companiesService';
import toast from 'react-hot-toast';
import { useEscapeModal, hasFormChanges } from '../utils/useEscapeModal';

interface ContactFormData {
  name: string;
  email: string;
  phone: string;
  title: string;
  department: string;
  company: string;
  linkedin: string;
  twitter: string;
  relationship: 'colleague' | 'manager' | 'hr' | 'recruiter' | 'friend' | 'referral' | 'other';
  howMet: 'linkedin' | 'conference' | 'referral' | 'networking_event' | 'online' | 'previous_job' | 'other';
  notes: string;
  tags: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  nextFollowUp: string;
  followUpNotes: string;
}

const Contacts: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [companyFilter, setCompanyFilter] = useState<string>('');
  const [relationshipFilter, setRelationshipFilter] = useState<string>('');

  const [formData, setFormData] = useState<ContactFormData>({
    name: '',
    email: '',
    phone: '',
    title: '',
    department: '',
    company: '',
    linkedin: '',
    twitter: '',
    relationship: 'other',
    howMet: 'other',
    notes: '',
    tags: '',
    priority: 'medium',
    nextFollowUp: '',
    followUpNotes: ''
  });

  // Initial form data for change detection
  const [initialFormData, setInitialFormData] = useState<ContactFormData>({
    name: '',
    email: '',
    phone: '',
    title: '',
    department: '',
    company: '',
    linkedin: '',
    twitter: '',
    relationship: 'other',
    howMet: 'other',
    notes: '',
    tags: '',
    priority: 'medium',
    nextFollowUp: '',
    followUpNotes: ''
  });

  const relationshipOptions = [
    { value: 'colleague', label: 'Colleague' },
    { value: 'manager', label: 'Manager' },
    { value: 'hr', label: 'HR' },
    { value: 'recruiter', label: 'Recruiter' },
    { value: 'friend', label: 'Friend' },
    { value: 'referral', label: 'Referral' },
    { value: 'other', label: 'Other' }
  ];

  const priorityOptions = [
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' },
    { value: 'urgent', label: 'Urgent' }
  ];

  useEffect(() => {
    fetchData();
  }, []);

  // Initialize filters from URL parameters
  useEffect(() => {
    const companyParam = searchParams.get('company');
    const searchParam = searchParams.get('search');
    const relationshipParam = searchParams.get('relationship');

    if (companyParam) {
      setCompanyFilter(companyParam);
    }
    if (searchParam) {
      setSearchTerm(searchParam);
    }
    if (relationshipParam) {
      setRelationshipFilter(relationshipParam);
    }
  }, [searchParams]);

  // Update URL when filters change
  useEffect(() => {
    const newSearchParams = new URLSearchParams();
    
    if (companyFilter) {
      newSearchParams.set('company', companyFilter);
    }
    if (searchTerm) {
      newSearchParams.set('search', searchTerm);
    }
    if (relationshipFilter) {
      newSearchParams.set('relationship', relationshipFilter);
    }

    setSearchParams(newSearchParams, { replace: true });
  }, [companyFilter, searchTerm, relationshipFilter, setSearchParams]);

  const fetchData = async (): Promise<void> => {
    try {
      setLoading(true);
      const [contactsRes, companiesRes] = await Promise.all([
        contactsService.getContacts({ limit: 100 }),
        companiesService.getCompanies({ limit: 100 })
      ]);

      setContacts(contactsRes.data);
      setCompanies(companiesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to fetch contacts');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        tags: formData.tags ? formData.tags.split(',').map(tag => tag.trim()) : []
      };

      if (editingContact) {
        await contactsService.updateContact(editingContact._id, submitData);
        toast.success('Contact updated successfully');
      } else {
        await contactsService.createContact(submitData);
        toast.success('Contact created successfully');
      }
      setShowModal(false);
      await fetchData();
    } catch (error) {
      console.error('Error saving contact:', error);
      toast.error('Failed to save contact');
    }
  };

  const handleEdit = (contact: Contact): void => {
    setEditingContact(contact);
    const editFormData = {
      name: contact.name || '',
      title: contact.title || '',
      department: contact.department || '',
      email: contact.email || '',
      phone: contact.phone || '',
      linkedin: contact.linkedin || '',
      twitter: contact.twitter || '',
      company: typeof contact.company === 'object' ? contact.company._id : contact.company || '',
      relationship: contact.relationship || 'other' as const,
      howMet: contact.howMet || 'other' as const,
      notes: contact.notes || '',
      tags: contact.tags ? contact.tags.join(', ') : '',
      priority: contact.priority || 'medium' as const,
      nextFollowUp: contact.nextFollowUp ? new Date(contact.nextFollowUp).toISOString().split('T')[0] : '',
      followUpNotes: contact.followUpNotes || ''
    };
    setFormData(editFormData);
    setInitialFormData(editFormData);
    setShowModal(true);
  };

  const handleDelete = async (id: string): Promise<void> => {
    if (window.confirm('Are you sure you want to delete this contact?')) {
      try {
        await contactsService.deleteContact(id);
        toast.success('Contact deleted successfully');
        await fetchData();
      } catch (error) {
        console.error('Error deleting contact:', error);
        toast.error('Failed to delete contact');
      }
    }
  };

  const resetForm = (): void => {
    const defaultFormData = {
      name: '',
      email: '',
      phone: '',
      title: '',
      department: '',
      company: '',
      linkedin: '',
      twitter: '',
      relationship: 'other' as const,
      howMet: 'other' as const,
      notes: '',
      tags: '',
      priority: 'medium' as const,
      nextFollowUp: '',
      followUpNotes: ''
    };

    setFormData(defaultFormData);
    setInitialFormData(defaultFormData);
    setEditingContact(null);
    setShowModal(false);
  };

  // Check if form has unsaved changes
  const hasUnsavedChanges = hasFormChanges(formData, initialFormData);

  // Handle ESC key to close modal with confirmation for unsaved changes
  useEscapeModal({
    isOpen: showModal,
    onClose: resetForm,
    hasUnsavedChanges,
    confirmMessage: 'You have unsaved changes. Are you sure you want to close without saving?'
  });

  const clearFilters = (): void => {
    setSearchTerm('');
    setCompanyFilter('');
    setRelationshipFilter('');
  };

  const getPriorityColor = (priority: string): string => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredContacts = contacts.filter(contact => {
    const matchesSearch = contact.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         contact.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         contact.title?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCompany = !companyFilter || (typeof contact.company === 'object' ? contact.company._id === companyFilter : contact.company === companyFilter);
    const matchesRelationship = !relationshipFilter || contact.relationship === relationshipFilter;
    return matchesSearch && matchesCompany && matchesRelationship;
  });

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">Contacts</h1>
          <p className="mt-1 text-sm text-secondary-600">
            Manage your professional network and connections.
          </p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          className="btn btn-primary btn-md flex items-center gap-2"
        >
          <HiOutlinePlus className="h-4 w-4" />
          Add Contact
        </button>
      </div>

      {/* Search and Filters */}
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <HiOutlineSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-secondary-400" />
            <input
              type="text"
              placeholder="Search contacts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input w-full pl-10"
            />
          </div>
          <select
            value={companyFilter}
            onChange={(e) => setCompanyFilter(e.target.value)}
            className="input w-full sm:w-48"
          >
            <option value="">All Companies</option>
            {companies.map(company => (
              <option key={company._id} value={company._id}>{company.name}</option>
            ))}
          </select>
          <select
            value={relationshipFilter}
            onChange={(e) => setRelationshipFilter(e.target.value)}
            className="input w-full sm:w-48"
          >
            <option value="">All Relationships</option>
            {relationshipOptions.map(option => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
          {(searchTerm || companyFilter || relationshipFilter) && (
            <button
              onClick={clearFilters}
              className="btn btn-secondary btn-sm whitespace-nowrap"
            >
              Clear Filters
            </button>
          )}
        </div>

        {/* Active Filters Info */}
        {(searchTerm || companyFilter || relationshipFilter) && (
          <div className="flex items-center gap-2 text-sm text-secondary-600">
            <span>Active filters:</span>
            {searchTerm && (
              <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs">
                Search: "{searchTerm}"
              </span>
            )}
            {companyFilter && (
              <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs">
                Company: {companies.find(c => c._id === companyFilter)?.name || 'Unknown'}
              </span>
            )}
            {relationshipFilter && (
              <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded-full text-xs">
                Relationship: {relationshipOptions.find(r => r.value === relationshipFilter)?.label || relationshipFilter}
              </span>
            )}
            <span className="text-secondary-500">
              ({filteredContacts.length} contact{filteredContacts.length === 1 ? '' : 's'} found)
            </span>
          </div>
        )}
      </div>

      {/* Contacts List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredContacts.map((contact) => (
          <div 
            key={contact._id} 
            onClick={() => navigate(`/contacts/${contact._id}`)}
            className="bg-white rounded-xl shadow-sm border border-secondary-200 hover:shadow-lg hover:border-primary-200 transition-all duration-200 overflow-hidden h-fit cursor-pointer"
          >
            {/* Header with name and actions */}
            <div className="bg-gradient-to-r from-primary-50 to-secondary-50 px-5 py-4 border-b border-secondary-100">
              <div className="flex justify-between items-start">
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-semibold text-secondary-900 truncate">
                    {contact.name}
                  </h3>
                  <p className="text-sm text-secondary-600 mt-1 truncate">
                    {contact.title} {contact.title && 'at'} {typeof contact.company === 'object' ? contact.company.name : contact.title ? 'Unknown Company' : 'No company'}
                  </p>
                </div>
                <div className="flex items-center gap-1 ml-3 flex-shrink-0">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/contacts/${contact._id}`);
                    }}
                    className="p-2 text-secondary-500 hover:text-primary-600 hover:bg-white rounded-lg transition-colors"
                    title="View contact"
                  >
                    <HiOutlineEye className="h-4 w-4" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEdit(contact);
                    }}
                    className="p-2 text-secondary-500 hover:text-primary-600 hover:bg-white rounded-lg transition-colors"
                    title="Edit contact"
                  >
                    <HiOutlinePencil className="h-4 w-4" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(contact._id);
                    }}
                    className="p-2 text-secondary-500 hover:text-red-600 hover:bg-white rounded-lg transition-colors"
                    title="Delete contact"
                  >
                    <HiOutlineTrash className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
            
            {/* Content */}
            <div className="p-5">
              {/* Priority and Relationship Badges */}
              <div className="flex items-center gap-2 mb-4">
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getPriorityColor(contact.priority || 'medium')}`}>
                  {priorityOptions.find(p => p.value === contact.priority)?.label || contact.priority}
                </span>
                {contact.relationship && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                    <HiOutlineUser className="h-3 w-3 mr-1" />
                    {relationshipOptions.find(r => r.value === contact.relationship)?.label || contact.relationship}
                  </span>
                )}
              </div>
              
              {/* Contact Information */}
              <div className="space-y-3">
                {contact.email && (
                  <div className="flex items-center gap-3 text-sm text-secondary-600">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <HiOutlineMail className="h-4 w-4 text-primary-500 flex-shrink-0" />
                      <span className="truncate">{contact.email}</span>
                    </div>
                  </div>
                )}
                {contact.phone && (
                  <div className="flex items-center gap-3 text-sm text-secondary-600">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <HiOutlinePhone className="h-4 w-4 text-primary-500 flex-shrink-0" />
                      <span className="truncate">{contact.phone}</span>
                    </div>
                  </div>
                )}
                {contact.linkedin && (
                  <div className="flex items-center gap-3 text-sm">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <HiOutlineExternalLink className="h-4 w-4 text-primary-500 flex-shrink-0" />
                      <a
                        href={contact.linkedin}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="text-primary-600 hover:text-primary-700 truncate font-medium"
                      >
                        LinkedIn Profile
                      </a>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Follow-up indicator */}
              {contact.nextFollowUp && (
                <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-center gap-2 text-sm text-amber-700">
                    <HiOutlineCalendar className="h-4 w-4" />
                    <span className="font-medium">Follow-up: {new Date(contact.nextFollowUp).toLocaleDateString()}</span>
                  </div>
                </div>
              )}
              
              {/* Notes */}
              {contact.notes && (
                <div className="mt-4 pt-4 border-t border-secondary-100">
                  <p 
                    className="text-sm text-secondary-600 leading-relaxed"
                    style={{
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden'
                    }}
                  >
                    {contact.notes}
                  </p>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {filteredContacts.length === 0 && (
        <div className="col-span-full">
          <div className="text-center py-12 bg-white rounded-lg border border-secondary-200">
            <HiOutlineUser className="mx-auto h-12 w-12 text-secondary-400" />
            <h3 className="mt-2 text-sm font-medium text-secondary-900">No contacts found</h3>
            <p className="mt-1 text-sm text-secondary-500">
              {searchTerm || companyFilter || relationshipFilter 
                ? 'Try adjusting your filters to find contacts.'
                : 'Get started by adding your first professional contact.'
              }
            </p>
            {!searchTerm && !companyFilter && !relationshipFilter && (
              <button
                onClick={() => {
                  resetForm();
                  setShowModal(true);
                }}
                className="mt-4 btn btn-primary btn-sm flex items-center gap-2 mx-auto"
              >
                <HiOutlinePlus className="h-4 w-4" />
                Add Your First Contact
              </button>
            )}
          </div>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-medium text-secondary-900 mb-4">
              {editingContact ? 'Edit Contact' : 'Add New Contact'}
            </h3>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-secondary-700 mb-1">
                    Name
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="input w-full"
                    placeholder="Contact name"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1">
                    Title
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="input w-full"
                    placeholder="Job title"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1">
                    Department
                  </label>
                  <input
                    type="text"
                    value={formData.department}
                    onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                    className="input w-full"
                    placeholder="Department"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="input w-full"
                    placeholder="email@example.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1">
                    LinkedIn Profile
                  </label>
                  <input
                    type="url"
                    value={formData.linkedin}
                    onChange={(e) => setFormData({ ...formData, linkedin: e.target.value })}
                    className="input w-full"
                    placeholder="https://linkedin.com/in/username"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-secondary-700 mb-1">
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="input w-full"
                    placeholder="Phone number"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-secondary-700 mb-1">
                    Company
                  </label>
                  <select
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    className="input w-full"
                  >
                    <option value="">Select a company</option>
                    {companies.map(company => (
                      <option key={company._id} value={company._id}>{company.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1">
                    Relationship
                  </label>
                  <select
                    value={formData.relationship}
                    onChange={(e) => setFormData({ ...formData, relationship: e.target.value as any })}
                    className="input w-full"
                  >
                    {relationshipOptions.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1">
                    Priority
                  </label>
                  <select
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: e.target.value as any })}
                    className="input w-full"
                  >
                    {priorityOptions.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-secondary-700 mb-1">
                    Notes
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={3}
                    className="input w-full"
                    placeholder="Add any notes about this contact..."
                  />
                </div>
              </div>

              <div className="flex gap-2 pt-6 mt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="btn btn-secondary flex-1 py-2.5 px-4 rounded-md hover:bg-gray-100 transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary flex-1 py-2.5 px-4 rounded-md hover:bg-blue-600 transition-colors duration-200"
                >
                  {editingContact ? 'Update' : 'Create'} Contact
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Contacts; 